import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Cancel,
  CheckCircle,
  Warning,
  Info,
  Payment,
  Schedule,
  MoneyOff,
  TrendingDown,
  Security,
  Receipt,
  Timeline,
  EventBusy
} from '@mui/icons-material';
import { cancellationRefundService } from '../services/firebase';
import { CancellationRefundData } from '../types';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../utils/getTranslatedData';

// Icon mapping for dynamic icon rendering
const getIcon = (iconName: string) => {
  const iconProps = { sx: { fontSize: 24 } };
  
  switch (iconName) {
    case 'Payment': return <Payment {...iconProps} />;
    case 'Receipt': return <Receipt {...iconProps} />;
    case 'MoneyOff': return <MoneyOff {...iconProps} />;
    case 'Schedule': return <Schedule {...iconProps} />;
    case 'Security': return <Security {...iconProps} />;
    case 'Timeline': return <Timeline {...iconProps} />;
    default: return <Payment {...iconProps} />;
  }
};

// Alert type icon mapping
const alertIconMap: { [key: string]: React.ReactElement } = {
  info: <Info />,
  warning: <Warning />,
  success: <CheckCircle />,
  error: <Warning />
};

const CancellationRefundPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [refundData, setRefundData] = useState<CancellationRefundData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRefundData = async () => {
      try {
        setLoading(true);
        let data = await cancellationRefundService.getCancellationRefundData();
        
        if (!data) {
          // Initialize with default data if no data exists
          await cancellationRefundService.initializeCancellationRefundData();
          data = await cancellationRefundService.getCancellationRefundData();
        }
        
        setRefundData(data);
      } catch (err: any) {
        setError('İptal ve iade politikası yüklenirken hata oluştu: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRefundData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const formattedDate = refundData?.lastUpdated 
    ? new Date(refundData.lastUpdated.toDate()).toLocaleDateString(i18n.language, { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })
    : '';

  return (
    <>
      <Helmet>
        <title>{getTranslatedData(refundData?.title) || t('static_pages.cancellation_refund')} - HatTravel</title>
        <meta name="description" content={getTranslatedData(refundData?.description) || t('static_pages.cancellation_refund_description')} />
      </Helmet>
      
      <Box sx={{ 
        backgroundColor: 'var(--light-gray-white)',
        minHeight: '100vh',
        py: 8
      }}>
        <Container maxWidth="lg">
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  backgroundColor: 'var(--light-blue-cta)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  boxShadow: '0 8px 24px rgba(0,91,189,0.2)'
                }}>
                  <Cancel sx={{ fontSize: 50 }} />
                </Box>
              </Box>
              
              <Typography 
                variant="h1" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '3rem', md: '4rem' },
                  color: 'var(--navy-blue)',
                  mb: 2
                }}
              >
                {getTranslatedData(refundData?.title) || 'İptal ve İade Şartları'}
              </Typography>
              
              <Typography 
                variant="h5" 
                color="text.secondary"
                sx={{ 
                  maxWidth: 700, 
                  mx: 'auto',
                  fontWeight: 300,
                  lineHeight: 1.6,
                  mb: 4
                }}
              >
                {getTranslatedData(refundData?.description) || 'Esnek iptal politikamız ile güvenle rezervasyon yapın.'}
              </Typography>

              <Chip
                icon={<EventBusy />}
                label={`${t('static_pages.last_updated')}: ${formattedDate}`}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 3,
                  fontWeight: 500,
                  fontSize: '1rem',
                  p: 2.5,
                  border: '1px solid var(--gray-shadow)'
                }}
              />
            </Box>

            {/* Important Notes */}
            {refundData?.importantNotes && refundData.importantNotes.length > 0 && (
              <Box sx={{ mb: 6 }}>
                {refundData.importantNotes
                  ?.filter(note => note.isActive)
                  ?.sort((a, b) => a.order - b.order)
                  ?.map((note) => (
                    <Alert
                      key={note.id}
                      severity={note.type}
                      icon={alertIconMap[note.type]}
                      sx={{
                        mb: 2,
                        borderRadius: 3,
                        '& .MuiAlert-message': {
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1
                        }
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {getTranslatedData(note.title)}
                      </Typography>
                      <Typography variant="body2">
                        {getTranslatedData(note.content)}
                      </Typography>
                    </Alert>
                  ))}
              </Box>
            )}

            {/* Cancellation Policies Table */}
            {refundData?.cancellationPolicies && refundData.cancellationPolicies.length > 0 && (
              <Card sx={{ mb: 6, borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ p: 4, backgroundColor: 'var(--light-gray-white)', borderBottom: '1px solid var(--gray-shadow)' }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'var(--navy-blue)', textAlign: 'center' }}>
                      {t('static_pages.cancellation_table_title')}
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'var(--light-gray-white)' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '1.1rem' }}>{t('static_pages.cancellation_period')}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '1.1rem' }}>{t('static_pages.refund_rate')}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '1.1rem' }}>{t('static_pages.conditions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {refundData.cancellationPolicies
                          ?.filter(policy => policy.isActive)
                          ?.sort((a, b) => a.order - b.order)
                          ?.map((policy) => (
                            <TableRow key={policy.id} sx={{ '&:hover': { backgroundColor: 'var(--light-gray-white)' } }}>
                              <TableCell sx={{ fontSize: '1rem', fontWeight: 500 }}>
                                {getTranslatedData(policy.period)}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getTranslatedData(policy.refund)}
                                  color={policy.color || 'default'}
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontSize: '1rem' }}>
                                {getTranslatedData(policy.conditions)}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* General Content */}
            {refundData?.content && (
              <Card sx={{ mb: 6, borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                  <Box
                    sx={{
                      color: 'var(--navy-blue)',
                      lineHeight: 1.8,
                      '& h3': {
                        fontWeight: 600,
                        fontSize: '1.3rem',
                        color: 'var(--light-blue-cta)',
                        mt: 3,
                        mb: 1.5,
                      },
                      '& p': { mb: 2 },
                      '& ul': { pl: 3, mb: 2 },
                      '& li': { mb: 1 },
                      '& strong': { fontWeight: 600, color: 'var(--light-blue-cta)' },
                    }}
                    dangerouslySetInnerHTML={{ __html: getTranslatedData(refundData.content, i18n.language) }}
                  />
                </CardContent>
              </Card>
            )}

            {/* Refund Methods */}
            {refundData?.refundMethods && refundData.refundMethods.length > 0 && (
              <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ p: 4, backgroundColor: 'var(--light-gray-white)', borderBottom: '1px solid var(--gray-shadow)' }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'var(--navy-blue)', textAlign: 'center' }}>
                      {t('static_pages.refund_methods_title')}
                    </Typography>
                  </Box>
                  <List sx={{ p: { xs: 2, md: 4 } }}>
                    {refundData.refundMethods
                      ?.filter(method => method.isActive)
                      ?.sort((a, b) => a.order - b.order)
                      ?.map((method) => (
                        <ListItem key={method.id} sx={{ 
                          mb: 2, 
                          backgroundColor: 'var(--light-gray-white)', 
                          borderRadius: 3,
                          p: 3,
                        }}>
                          <ListItemIcon sx={{ minWidth: 50 }}>
                            <Box sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              backgroundColor: `var(--${method.color || 'light-blue-cta'})`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white'
                            }}>
                              {getIcon(method.icon)}
                            </Box>
                          </ListItemIcon>
                          <ListItemText 
                            primary={getTranslatedData(method.method)}
                            secondary={getTranslatedData(method.time)}
                            primaryTypographyProps={{ fontWeight: 600, fontSize: '1.2rem', color: 'var(--navy-blue)' }}
                            secondaryTypographyProps={{ fontSize: '1rem', color: 'text.secondary' }}
                          />
                        </ListItem>
                      ))}
                  </List>
                </CardContent>
              </Card>
            )}
        </Container>
      </Box>
    </>
  );
};

export default CancellationRefundPage;